import { APIService, APIMockService, tryRes } from "services";
import type { Dealer } from "models";
import type { DealerPartner, PurchasePartnerWithBrands } from "models/Dealer";
import { delay } from "utils";

export interface BrandsResponse {
  direct: PurchasePartnerWithBrands;
  throughBuyingGroup: PurchasePartnerWithBrands;
  throughDistribution: {
    distributorCustomerAccount: PurchasePartnerWithBrands[];
  };
  dealerId: string;
}

export interface SubDealerResponse {
  customerName: string;
  customerNumber: string;
  customerCompany: string;
  customerBranch: string;
}

export interface ProductInfo {
  product: string;
  name: string;
  id: string;
}

interface Brand {
  name: string;
  code: number;
  isDefault: boolean;
  brands: ProductInfo[];
}

type BrandsDirect = Brand;
type BrandsBuyingGroup = Brand;
type BrandsDistribution = { distributorCustomerAccount: BrandsDistribution };

export interface BrandsInfo {
  direct: BrandsDirect;
  throughBuyingGroup: BrandsBuyingGroup;
  throughDistribution: BrandsDistribution;
}

class DealerMockService extends APIMockService<Dealer> {
  async getDealers(): Promise<Dealer[]> {
    return delay([]);
  }

  async getBrands(): Promise<BrandsResponse> {
    return delay({} as BrandsResponse);
  }
}
class DealerService extends APIService<Dealer> {
  async getDealers() {
    const response = await fetch(this._endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<Dealer[]>(response);
  }

  async getBrands(): Promise<BrandsResponse> {
    const response = await fetch(`${this._endpoint}/mine/partners/brands`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<BrandsResponse>(response);
  }

  async getAllBrands(): Promise<BrandsResponse[]> {
    const response = await fetch(`${this._endpoint}/partners/brands`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<BrandsResponse[]>(response);
  }

  async getBrandsById(rewardsId: string): Promise<BrandsResponse> {
    const response = await fetch(`${this._endpoint}/${rewardsId}/partners/brands`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<BrandsResponse>(response);
  }

  async getSubDealer(dealerId: string): Promise<SubDealerResponse[]> {
    const response = await fetch(`${this._endpoint}/${dealerId}/subdealers`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<SubDealerResponse[]>(response);
  }

  async addOrRefreshDealer(rewardsId: string): Promise<DealerPartner> {
    const response = await fetch(`${this._endpoint}/${rewardsId}`, {
      method: "PUT",
    });
    return tryRes<DealerPartner>(response);
  }

  async addOrRefreshUserDealer(dealer: Dealer): Promise<Dealer> {
    const response = await fetch(`${this._endpoint}/user`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dealer),
    });
    return tryRes<Dealer>(response);
  }

  async updateDealer(dealer: Dealer): Promise<any> {
    const response = await fetch(`${this._endpoint}/${dealer.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dealer),
    });
    return tryRes<Dealer>(response);
  }

  async getMyDefaultPartnerCode(): Promise<number | null> {
    const response = await fetch(`${this._endpoint}/mine/partners`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const dealerPartners = await tryRes<any>(response);
    return dealerPartners?.defaultPartnerCode || null;
  }

  async getDealerDefaultPartnerCode(dealerId: string): Promise<number | null> {
    const response = await fetch(`${this._endpoint}/${dealerId}/defaultPartner`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<number | null>(response);
  }

  async setDealerDefaultPartnerCode(
    dealerId: string,
    code: string,
  ): Promise<{ success: boolean; code: number } | null> {
    const response = await fetch(
      `${this._endpoint}/${dealerId}/defaultPartner/${code}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      },
    );

    return tryRes<{ success: boolean; code: number } | null>(response);
  }

  async getPurchasePartners(rewardsId: string): Promise<DealerPartner> {
    const response = await fetch(`${this._endpoint}/${rewardsId}/partners/info`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return tryRes<DealerPartner>(response);
  }
}

const service = new DealerService("dealers");

export default service;
